'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  PropsWithChildren,
} from 'react'
import { useSession } from '@/features/auth/context/session.context'
import { evaluateFeatureFlags } from '../api/feature-flags.api'

interface FeatureFlagsContextType {
  flags: Record<string, boolean>
  isFeatureEnabled: (key: string) => boolean
  isLoading: boolean
  refreshFlags: () => Promise<void>
}

export const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null)

export function FeatureFlagsProvider({ children }: PropsWithChildren) {
  const { session, isLoading: isSessionLoading } = useSession()
  const [flags, setFlags] = useState<Record<string, boolean>>({})
  const [isLoading, setIsLoading] = useState(true)

  const loadFlags = useCallback(async () => {
    try {
      setIsLoading(true)
      const res = await evaluateFeatureFlags()
      if (res.success && res.data) {
        setFlags(res.data)
      } else {
        setFlags({})
      }
    } catch (err) {
      console.error('Failed to load feature flags:', err)
      setFlags({})
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // When session is done loading or when user ID changes, evaluate flags
    if (!isSessionLoading) {
      loadFlags()
    }
  }, [isSessionLoading, session?.sub, loadFlags])

  const isFeatureEnabled = useCallback(
    (key: string): boolean => {
      const normalizedKey = key.trim().toLowerCase()
      return !!flags[normalizedKey]
    },
    [flags],
  )

  return (
    <FeatureFlagsContext.Provider
      value={{
        flags,
        isFeatureEnabled,
        isLoading,
        refreshFlags: loadFlags,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  )
}

export function useFeatureFlagsContext(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider')
  }
  return context
}
